import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardMedia, Grid, Box, Button, Alert, TextField, Snackbar } from '@mui/material';

const ShortBox = () => {
  const [books, setBooks] = useState([]);
  const [searchBooks, setSearchBooks] = useState([]);
  const [lastWatchSession, setLastWatchSession] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [page, setPage] = useState(2);
  const [isFetching, setIsFetching] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const navigate = useNavigate();

  // Function to fetch books from the API
  const fetchBooks = async (url) => {
    try {
      const response = await fetch(url);
      const result = await response.json();
      if (result.ok) {
        return result.data?.data || [];
      }
      throw new Error('Error fetching data');
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // Initial books load
  useEffect(() => {
    const loadInitialBooks = async () => {
      const url = `https://shortbox.tv/api/collection/list?locale=id&pageNum=1&pageSize=10`;
      const data = await fetchBooks(url);
      setBooks(data);
    };

    if (!debouncedSearchValue) {
      loadInitialBooks();
    }
  }, [debouncedSearchValue]);

  // Fetch books on search
  useEffect(() => {
    if (debouncedSearchValue) {
      const searchBooks = async () => {
        const searchUrl = `https://shortbox.tv/api/collection/search?keyword=${debouncedSearchValue}&locale=id&pageNum=1&pageSize=100`;
        const data = await fetchBooks(searchUrl);
        setSearchBooks(data);
      };
      searchBooks();
      setPage(2);
    }
  }, [debouncedSearchValue]);

  // Handle debounced search value change
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Load more books on scroll
  useEffect(() => {
    if (!isFetching || debouncedSearchValue) return;

    const loadMoreBooks = async () => {
      const url = `https://shortbox.tv/api/collection/list?locale=id&pageNum=${page}&pageSize=10`;
      const data = await fetchBooks(url);

      if (data.length > 0) {
        setBooks((prevBooks) => [...prevBooks, ...data]);
        setPage((prevPage) => prevPage + 1);
      } else {
        setAlertMessage('Data sudah habis');
        setIsAlertOpen(true);
      }
      setIsFetching(false);
    };

    setAlertMessage('Loading...');
    setIsAlertOpen(true);
    loadMoreBooks();
  }, [isFetching, page, debouncedSearchValue]);

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight - 100 &&
        !isFetching &&
        !debouncedSearchValue
      ) {
        setIsFetching(true);
      }
    };

    let timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(handleScroll, 200);
    };

    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [isFetching, debouncedSearchValue]);

  useEffect(() => {
    const sessions = JSON.parse(localStorage.getItem('shortbox')) || [];
    if (sessions.length > 0) {
      const lastSession = sessions[sessions.length - 1];
      setLastWatchSession(lastSession);
    }
  }, []);

  const handleContinueWatching = () => {
    if (lastWatchSession) {
      const { bookId, bookName } = lastWatchSession;
      navigate(`/shortbox/${bookId}/${bookName}`);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      {lastWatchSession && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleContinueWatching}>
              Lanjutkan
            </Button>
          }
          sx={{ marginBottom: 2 }}
        >
          Anda terakhir menonton <strong>{lastWatchSession.bookName}</strong>.
        </Alert>
      )}

      <TextField
        label="Cari Drama"
        variant="outlined"
        fullWidth
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        sx={{ marginBottom: 2 }}
      />

      <Grid container spacing={2}>
        {(debouncedSearchValue ? searchBooks : books).map((book, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <Card
              sx={{
                position: 'relative',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
              component={Link}
              to={`/shortbox/${book.id}/${book.title}`}
            >
              <CardMedia
                component="img"
                sx={{
                  height: '100%',
                  objectFit: 'fill',
                }}
                image={book.cover}
                alt={book.title}
              />

              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRadius: '0 0 4px 4px',
                }}
              >
                {book.title}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar
        open={isAlertOpen}
        autoHideDuration={1500}
        onClose={() => setIsAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setIsAlertOpen(false)} severity="info" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShortBox;
